// src/contexts/NotificationContext.tsx
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { Notification } from "../models/Notification";
import { useAuth } from "./AuthContext";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { useMatter } from "./MatterContext";
import { NotificationService } from "../services/NotificationService";

interface NotificationContextProps {
  notifications: Notification[];
  updateNotification: (
    notificationId: string,
    updatedData: Partial<Omit<Notification, "_id" | "createdAt">>,
  ) => Promise<string>;
  loading: boolean;
  error: string | null;
}

const NotificationContext = createContext<NotificationContextProps | undefined>(
  undefined,
);

export const useNotification = (): NotificationContextProps => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotification must be used within a NotificationProvider",
    );
  }
  return context;
};

export const NotificationProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    const docRef = query(
      collection(db, "notifications"),
      where("userId", "==", user.uid),
    );
    const unsubscribe = onSnapshot(
      docRef,
      (docSnapshot) => {
        setLoading(false);
        if (docSnapshot.empty) {
          setError("Notifications not found.");
          return;
        }
        setNotifications(
          docSnapshot.docs.map(
            (doc) => ({ _id: doc.id, ...doc.data() }) as Notification,
          ),
        );
      },
      (err) => {
        setError(`Failed to listen to notification changes: ${err.message}`);
      },
    );
    return () => {
      unsubscribe();
    };
  }, [user]);

  const updateNotification = async (
    notificationId: string,
    updatedData: Partial<Omit<Notification, "_id" | "createdAt">>,
  ): Promise<string> => {
    await NotificationService.updateNotification(notificationId, updatedData);
    return notificationId;
  };

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        loading,
        error,
        updateNotification,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};
